import type {
  DatabaseBulkFailure,
  DatabaseBulkProgress,
  DatabaseBulkReceiptStatus,
  DatabaseBulkResult,
} from '@streamlain/database-identity';
import {
  DatabaseBulkBudgetError,
  settleDatabaseBulkReceipt,
} from '@streamlain/database-identity';
import { PublicationError } from '@streamlain/doc-publication-contract';

export type DatabaseClientBulkRequest = {
  kind: 'update' | 'delete';
  rowIds: readonly string[];
  values?: Readonly<Record<string, unknown>>;
  signal?: AbortSignal;
  onProgress?: (progress: DatabaseBulkProgress) => void;
};
export type DatabaseClientBulkPort =
  | {
      mode: 'cloud';
      readReceipt?(input: {
        databaseId: string;
        accessDocId: string;
        groupId: string;
        operationId: string;
        signal: AbortSignal;
      }): Promise<DatabaseBulkReceiptStatus>;
      subscribeReceipts?(callback: () => void): () => void;
      mutate(
        request: DatabaseClientBulkRequest & {
          operationId: string;
          databaseId: string;
          accessDocId?: string;
        }
      ): Promise<DatabaseBulkResult>;
    }
  | {
      mode: 'local';
      retain(
        docIds: readonly string[],
        signal: AbortSignal
      ): Promise<() => void>;
      acknowledge(
        docIds: readonly string[],
        signal: AbortSignal
      ): Promise<void>;
    };
function isBudgetRejection(error: unknown): boolean {
  return (
    error instanceof DatabaseBulkBudgetError ||
    (error instanceof PublicationError && error.code === 'LIMIT_EXCEEDED')
  );
}
export const databaseBulkProviders = new WeakMap<
  object,
  DatabaseClientBulkPort
>();
export type PreparedDatabaseBulkRows = {
  rowIds: readonly string[];
  docIds: readonly string[];
  release(): void;
};
export type DatabaseClientBulkContext = {
  databaseId: string;
  operationId: string;
  deadlineAt: number;
  assertCanWrite(): void;
  prepare(
    rowIds: readonly string[],
    signal: AbortSignal
  ): Promise<PreparedDatabaseBulkRows>;
  /** Settles with the local commit of exactly this write's own groups. */
  write(
    rowIds: readonly string[],
    request: DatabaseClientBulkRequest,
    signal: AbortSignal
  ): Promise<void>;
  port:
    | Pick<
        Extract<DatabaseClientBulkPort, { mode: 'local' }>,
        'mode' | 'acknowledge'
      >
    | Extract<DatabaseClientBulkPort, { mode: 'cloud' }>;
};

/** Logical facade: chunk costs and retained documents never reach UI callers. */
export async function executeDatabaseClientBulk(
  request: DatabaseClientBulkRequest,
  context: DatabaseClientBulkContext
): Promise<DatabaseBulkResult> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  request.signal?.addEventListener('abort', abort, { once: true });
  if (request.signal?.aborted) abort();
  const timer = setTimeout(abort, Math.max(0, context.deadlineAt - Date.now()));
  let progress: Omit<DatabaseBulkProgress, 'error'> = {
    operationId: context.operationId,
    databaseId: context.databaseId,
    kind: request.kind,
    iteratorExhausted: false,
    acknowledgement:
      context.port.mode === 'local' ? 'local-storage' : 'doc-persist',
    confirmedCount: 0,
    skippedChanged: 0,
    skippedMissing: 0,
    receipt: null,
  };
  const fence = () => {
    if (Date.now() >= context.deadlineAt)
      throw Object.assign(new Error('Operation deadline exceeded.'), {
        bulkCode: 'DEADLINE_EXCEEDED',
      });
    if (controller.signal.aborted)
      throw Object.assign(new Error('Operation cancelled.'), {
        bulkCode: 'CANCELLED',
      });
    try {
      context.assertCanWrite();
    } catch {
      throw Object.assign(new Error('Write access is unavailable.'), {
        bulkCode: 'ACCESS_REVOKED',
      });
    }
  };
  const rows = [...new Set(request.rowIds)];
  try {
    fence();
    if (
      rows.length > 5000 ||
      (request.kind === 'update' && !Object.keys(request.values ?? {}).length)
    )
      throw Object.assign(new Error('Invalid row operation.'), {
        bulkCode: 'INVALID_INPUT',
      });
    if (context.port.mode === 'cloud') {
      const result = await context.port.mutate({
        ...request,
        rowIds: rows,
        signal: controller.signal,
        operationId: context.operationId,
        databaseId: context.databaseId,
      });
      progress = result;
      try {
        request.onProgress?.(result);
      } catch (error) {
        if (result.status === 'pending') return result;
        throw Object.assign(
          error instanceof Error
            ? error
            : new Error('Progress observer failed.'),
          { bulkCode: 'POST_ACK_FAILURE' }
        );
      }
      return result;
    }
    const write = async (requested: readonly string[]): Promise<void> => {
      fence();
      const prepared = await context.prepare(requested, controller.signal);
      let released = false;
      let written = false;
      let acknowledged = false;
      try {
        // Same existing mutation-group document ceiling, including the root.
        if (new Set(prepared.docIds).size > 64) {
          if (requested.length === 1)
            throw Object.assign(
              new Error('The row operation exceeds its write budget.'),
              { bulkCode: 'DATABASE_BULK_BUDGET_EXCEEDED' }
            );
          const half = Math.ceil(requested.length / 2);
          // Release this cost probe before retaining the smaller windows.
          prepared.release();
          released = true;
          await write(requested.slice(0, half));
          await write(requested.slice(half));
          return;
        }
        fence();
        if (prepared.rowIds.length) {
          await context.write(prepared.rowIds, request, controller.signal);
          written = true;
          if (context.port.mode === 'local')
            await context.port.acknowledge(prepared.docIds, controller.signal);
          acknowledged = true;
          progress = {
            ...progress,
            confirmedCount: progress.confirmedCount + prepared.rowIds.length,
          };
        }
        progress = {
          ...progress,
          skippedMissing:
            progress.skippedMissing + requested.length - prepared.rowIds.length,
        };
        request.onProgress?.({ ...progress });
      } catch (error) {
        // A rejected local group commits nothing, so a divisible bulk request
        // may retry in smaller atomic groups; one row is never split further.
        if (!written && isBudgetRejection(error) && requested.length > 1) {
          prepared.release();
          released = true;
          const half = Math.ceil(requested.length / 2);
          await write(requested.slice(0, half));
          await write(requested.slice(half));
          return;
        }
        if (acknowledged)
          throw Object.assign(
            error instanceof Error
              ? error
              : new Error('Progress observer failed.'),
            { bulkCode: 'POST_ACK_FAILURE' }
          );
        throw error;
      } finally {
        if (!released) prepared.release();
      }
    };
    for (let offset = 0; offset < rows.length; offset += 100)
      await write(rows.slice(offset, offset + 100));
    return { ...progress, iteratorExhausted: true, status: 'completed' };
  } catch (error) {
    const explicit = (error as { bulkCode?: DatabaseBulkFailure['code'] })
      ?.bulkCode;
    const code =
      explicit ??
      (error instanceof PublicationError && error.code === 'CANCELLED'
        ? 'CANCELLED'
        : undefined) ??
      (isBudgetRejection(error)
        ? 'DATABASE_BULK_BUDGET_EXCEEDED'
        : undefined) ??
      (Date.now() >= context.deadlineAt
        ? 'DEADLINE_EXCEEDED'
        : controller.signal.aborted
          ? 'CANCELLED'
          : 'WRITE_FAILED');
    return {
      ...progress,
      status: code === 'CANCELLED' ? 'cancelled' : 'partial',
      error: {
        code,
        message:
          error instanceof Error ? error.message : 'Row operation failed.',
        retryable: false,
      },
    };
  } finally {
    clearTimeout(timer);
    request.signal?.removeEventListener('abort', abort);
  }
}

/** One admitted receipt per source. Reads settle it; writes are never replayed. */
export class DatabaseClientBulkReceiptOwner {
  private pending?: DatabaseBulkResult & { status: 'pending' };
  private port?: Extract<DatabaseClientBulkPort, { mode: 'cloud' }>;
  private binding?: { databaseId: string; accessDocId: string };
  private readonly abort = new AbortController();
  private stop?: () => void;
  private reading?: Promise<DatabaseBulkResult | undefined>;
  private dirty = false;
  constructor(private readonly publish: (value: DatabaseBulkResult) => void) {}

  remember(
    result: DatabaseBulkResult,
    port: Extract<DatabaseClientBulkPort, { mode: 'cloud' }>,
    binding: { databaseId: string; accessDocId: string }
  ) {
    if (result.status !== 'pending' || this.abort.signal.aborted) return;
    if (this.pending)
      throw new Error('An admitted database receipt is already owned.');
    if (result.databaseId !== binding.databaseId)
      throw new Error('Bulk database identity mismatch.');
    this.pending = result;
    this.port = port;
    this.binding = binding;
    try {
      this.stop = port.subscribeReceipts?.(() => {
        this.dirty = true;
        void this.refresh();
      });
    } catch {
      // Subscription availability cannot revoke an already admitted receipt.
      // User actions still refresh the same identity without replaying writes.
    }
    void this.refresh();
  }

  refresh(): Promise<DatabaseBulkResult | undefined> {
    if (this.reading) return this.reading;
    if (!this.pending || this.abort.signal.aborted)
      return Promise.resolve(this.pending);
    const read = async () => {
      let result: DatabaseBulkResult | undefined = this.pending;
      do {
        this.dirty = false;
        const pending = this.pending;
        if (!pending || !this.port?.readReceipt || !this.binding) return result;
        try {
          const receipt = await this.port.readReceipt({
            ...this.binding,
            groupId: pending.receipt.groupId,
            operationId: pending.receipt.operationId,
            signal: this.abort.signal,
          });
          if (this.abort.signal.aborted || this.pending !== pending)
            return pending;
          result = settleDatabaseBulkReceipt(pending, receipt);
          this.pending = result.status === 'pending' ? result : undefined;
          if (!this.pending) {
            this.stop?.();
            this.stop = undefined;
          }
          this.publish(result);
        } catch (error) {
          // A read failure cannot revoke admission or fabricate an ACK. Retain
          // its identity so reconnect/update/user actions can read it again.
          if (!this.abort.signal.aborted && this.pending === pending) {
            this.pending = {
              ...pending,
              error: {
                ...pending.error,
                message:
                  error instanceof Error
                    ? error.message
                    : 'Receipt could not be verified.',
              },
            };
            result = this.pending;
            this.publish(result);
          }
        }
      } while (this.dirty && this.pending && !this.abort.signal.aborted);
      return result;
    };
    const reading = read();
    this.reading = reading;
    const clear = () => {
      if (this.reading === reading) this.reading = undefined;
    };
    void reading.then(clear, clear);
    return reading;
  }

  dispose() {
    this.abort.abort();
    this.stop?.();
    this.stop = undefined;
    this.pending = undefined;
    this.port = undefined;
    this.binding = undefined;
  }
}
